import { h, Component } from 'preact';
import { Text, Localizer } from 'preact-i18n';
import { v4 as uuidv4 } from 'uuid';

import {
    CAT_VIEWBOX,
    CAT_TEETH,
    CAT_DECIDUOUS,
    CatChartStatic,
    CatChartLabels,
} from './dentalChartCat';
import {
    DOG_VIEWBOX,
    DOG_TEETH,
    DOG_DECIDUOUS,
    DogChartStatic,
    DogChartLabels,
} from './dentalChartDog';
import {
    HORSE_VIEWBOX,
    HORSE_TEETH,
    HorseChartStatic,
    HorseChartLabels,
} from './dentalChartHorse';

const TOOTH_COLORS = {
    incisor: '#95c99b',
    canine: '#ffe31a',
    premolar: '#4fc1ed',
    molar: '#f2b12b',
};

const STATUS_DEFS = [
    { code: 'normal', labelEn: 'Normal', labelNo: 'Normal', color: '#198754' },
    { code: 'missing', labelEn: 'Missing', labelNo: 'Mangler', color: '#888888' },
    { code: 'extraction', labelEn: 'Extraction', labelNo: 'Trekking', color: '#ed1c24' },
    { code: 'fracture', labelEn: 'Fracture', labelNo: 'Fraktur', color: '#ff8800' },
    { code: 'gingivitis', labelEn: 'Gingivitis', labelNo: 'Gingivitt', color: '#cc0066' },
    { code: 'calculus', labelEn: 'Calculus', labelNo: 'Tannstein', color: '#996600' },
    { code: 'mobility', labelEn: 'Mobility', labelNo: 'Mobilitet', color: '#9933cc' },
    { code: 'restoration', labelEn: 'Restoration', labelNo: 'Fylling', color: '#3366cc' },
    { code: 'caries', labelEn: 'Caries', labelNo: 'Karies', color: '#660033' },
    { code: 'recession', labelEn: 'Gingival recession', labelNo: 'Tilbaketrukket tannkjøtt', color: '#20c997' },
    { code: 'pocket', labelEn: 'Pocket (mm)', labelNo: 'Lomme (mm)', color: '#0dcaf0' },
    { code: 'malformedRoot', labelEn: 'Malformed root', labelNo: 'Misdannet rot', color: '#6610f2' },
    { code: 'resorption', labelEn: 'Tooth resorption', labelNo: 'Tannresorpsjon', color: '#795548' },
    { code: 'periodontitis', labelEn: 'Periodontitis', labelNo: 'Periodontitt', color: '#ad1457' },
    { code: 'furcation', labelEn: 'Furcation', labelNo: 'Furkasjon', color: '#00838f' },
    // Catch-all so a finding that has no matching tilstand can still be tagged as
    // something rather than being left blank (and rendered as "Normal").
    { code: 'other', labelEn: 'Other', labelNo: 'Annet', color: '#607d8b' },
];

const STATUS_LOOKUP = STATUS_DEFS.reduce((m, s) => { m[s.code] = s; return m; }, {});

const STYLES = `
.dc-jaw { fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.dc-detail { fill: none; stroke: currentColor; stroke-width: 1.2; stroke-linecap: round; stroke-linejoin: round; opacity: .55; }
.dc-leader { fill: none; stroke: #ed1c24; stroke-width: 2; stroke-linecap: square; stroke-linejoin: miter; }
.dc-label { font-family: Arial, Helvetica, sans-serif; font-size: 16px; font-weight: 700; fill: currentColor; }
.dc-jaw-label { font-family: Arial, Helvetica, sans-serif; font-size: 14px; font-weight: 700; fill: currentColor; }
.dc-tooth { stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; cursor: pointer; transition: stroke-width .1s; }
.dc-tooth-readonly { cursor: default; }
.dc-tooth:hover:not(.dc-tooth-readonly) { stroke-width: 3; }
.dc-tooth-selected { stroke: #0066ff !important; stroke-width: 3.5 !important; }
.dc-decid { cursor: pointer; transition: stroke-width .1s; }
.dc-decid-readonly { cursor: default; }
.dc-decid:hover:not(.dc-decid-readonly) { stroke-width: 2.6; }
.dc-decid-selected { stroke: #0066ff !important; stroke-width: 3 !important; }
.dc-decid-label { font-family: Arial, Helvetica, sans-serif; font-size: 13px; font-weight: 700; fill: currentColor; pointer-events: none; }
`;

// Size of the clickable deciduous (melketann) label box, TICKET-01850.
const DECID_BOX_W = 38;
const DECID_BOX_H = 18;

/**
 * Modified Triadan: the deciduous quadrant is the permanent quadrant + 4
 * (1xx -> 5xx ... 4xx -> 8xx). Only incisors (01-03), canines (04) and the three
 * distal premolars (06-08) have a deciduous predecessor; the first premolar
 * (05) and the molars (09+) do not. Returns null when there is none.
 */
function deciduousIdFor(permanentId) {
    const id = String(permanentId || '');
    if (!/^[1-4](0[1-4]|0[6-8])$/.test(id)) return null;
    return String(Number(id[0]) + 4) + id.slice(1);
}

function isDeciduousId(toothId) {
    return /^[5-8]\d\d$/.test(String(toothId || ''));
}

function permanentIdFor(deciduousId) {
    const id = String(deciduousId || '');
    if (!/^[5-8](0[1-4]|0[6-8])$/.test(id)) return null;
    return String(Number(id[0]) - 4) + id.slice(1);
}

function isCatSpecies(species, speciesId) {
    if (Number(speciesId) === 2) return true;
    if (!species) return false;
    const s = String(species).toLowerCase();
    return s === 'cat' || s === 'katt' || s === 'felis catus' || s === 'feline';
}

function isDogSpecies(species, speciesId) {
    if (Number(speciesId) === 1) return true;
    if (!species) return false;
    const s = String(species).toLowerCase();
    return s === 'dog' || s === 'hund' || s === 'canis' || s === 'canine' || s === 'canis lupus familiaris';
}

function isHorseSpecies(species, speciesId) {
    if (Number(speciesId) === 9) return true;
    if (!species) return false;
    const s = String(species).toLowerCase();
    return s === 'horse' || s === 'hest' || s === 'equus' || s === 'equus caballus' || s === 'equine';
}

// --- selection helpers -------------------------------------------------------
// The multi-select rules live in pure functions so they can be unit tested
// without a DOM: the component only keeps the list of selected tooth ids and
// hands the actual work to these.

/**
 * Ctrl/Cmd/Shift-click toggles a tooth in or out of the current selection.
 * A plain click narrows the selection down to the clicked tooth, and clicking
 * the only selected tooth again clears it (which closes the picker).
 */
function nextSelection(selected = [], toothId, additive = false) {
    if (additive) {
        return selected.includes(toothId)
            ? selected.filter(t => t !== toothId)
            : [...selected, toothId];
    }
    if (selected.length === 1 && selected[0] === toothId) {
        return [];
    }
    return [toothId];
}

/**
 * Writes `statusCode` to every tooth in `teeth`, creating findings for the teeth
 * that do not have one yet. Returns a new array - `findings` is never mutated.
 */
function applyStatusToTeeth(findings = [], teeth = [], statusCode) {
    if (!teeth.length) return findings;
    const code = statusCode || 'normal';
    const targets = new Set(teeth);
    const next = findings.map(f => (targets.has(f.tooth) ? { ...f, status: code } : f));
    const seen = new Set(findings.map(f => f.tooth));
    teeth.forEach((tooth) => {
        if (seen.has(tooth)) return;
        seen.add(tooth);
        next.push({ uuidv4: uuidv4(), tooth, status: code, note: '' });
    });
    return next;
}

/**
 * Writes the same note to every tooth in `teeth`. A tooth without a finding gets
 * one with an empty status - the vet typed a note before picking a tilstand, and
 * that must not end up rendered as "Normal" (TICKET-01400).
 */
function applyNoteToTeeth(findings = [], teeth = [], note) {
    if (!teeth.length) return findings;
    const targets = new Set(teeth);
    const next = findings.map(f => (targets.has(f.tooth) ? { ...f, note } : f));
    const seen = new Set(findings.map(f => f.tooth));
    teeth.forEach((tooth) => {
        if (seen.has(tooth)) return;
        seen.add(tooth);
        next.push({ uuidv4: uuidv4(), tooth, status: '', note });
    });
    return next;
}

function sortTeeth(teeth = []) {
    return [...teeth].sort((a, b) => String(a).localeCompare(String(b)));
}

class DentalChart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedTeeth: [],
        };
    }

    handleToothClick = (toothId, e) => {
        if (this.props.readOnly) return;
        const additive = !!(e && (e.ctrlKey || e.metaKey || e.shiftKey));
        this.setState(({ selectedTeeth }) => ({
            selectedTeeth: nextSelection(selectedTeeth, toothId, additive),
        }));
    };

    findingFor = (toothId) => {
        const { findings = [] } = this.props;
        return findings.find(f => f.tooth === toothId);
    };

    emitChange = (newFindings) => {
        const { onChange } = this.props;
        if (typeof onChange === 'function') {
            onChange(newFindings);
        }
    };

    setStatus = (statusCode) => {
        const { selectedTeeth } = this.state;
        const { findings = [] } = this.props;
        if (!selectedTeeth.length) return;
        this.emitChange(applyStatusToTeeth(findings, selectedTeeth, statusCode));
    };

    setNote = (e) => {
        const { selectedTeeth } = this.state;
        const { findings = [] } = this.props;
        if (!selectedTeeth.length) return;
        this.emitChange(applyNoteToTeeth(findings, selectedTeeth, e.target.value));
    };

    closePicker = () => {
        this.setState({ selectedTeeth: [] });
    };

    renderTooth = (tooth) => {
        const { readOnly } = this.props;
        const { selectedTeeth } = this.state;
        const finding = this.findingFor(tooth.id);
        const baseColor = TOOTH_COLORS[tooth.kind];
        const statusDef = finding && finding.status ? STATUS_LOOKUP[finding.status] : null;

        let fill = baseColor;
        if (statusDef && finding.status === 'missing') fill = '#dddddd';

        let stroke = '#000000';
        let strokeWidth = 1.6;
        if (statusDef) {
            stroke = statusDef.color;
            strokeWidth = 2.6;
        }

        const isSelected = selectedTeeth.includes(tooth.id);
        const cls = `dc-tooth${readOnly ? ' dc-tooth-readonly' : ''}${isSelected ? ' dc-tooth-selected' : ''}`;

        const handleClick = readOnly ? undefined : e => this.handleToothClick(tooth.id, e);

        const common = {
            id: `t-${tooth.id}`,
            'data-tooth': tooth.id,
            'data-tooth-class': tooth.kind,
            class: cls,
            fill,
            stroke,
            'stroke-width': strokeWidth,
            onClick: handleClick,
        };

        if (tooth.path) {
            return <path key={tooth.id} {...common} d={tooth.path} />;
        }
        return (
            <ellipse
                key={tooth.id}
                {...common}
                cx={tooth.cx}
                cy={tooth.cy}
                rx={tooth.rx}
                ry={tooth.ry}
                transform={`rotate(${tooth.rot} ${tooth.cx} ${tooth.cy})`}
            />
        );
    };

    // A deciduous tooth is drawn as a small numbered box beside the permanent
    // label rather than as a second tooth shape; the finding on it is keyed by
    // its own Triadan id (501 ...) so it lives next to the permanent finding
    // (101) instead of replacing it.
    renderDeciduous = (box) => {
        const { readOnly } = this.props;
        const { selectedTeeth } = this.state;
        const finding = this.findingFor(box.id);
        const statusDef = finding && finding.status ? STATUS_LOOKUP[finding.status] : null;

        // Colours go on the attributes, not the CSS class: a stylesheet rule
        // would beat the per-finding presentation attributes.
        let stroke = 'currentColor';
        let strokeWidth = 1.2;
        let fill = 'none';
        if (statusDef) {
            stroke = statusDef.color;
            strokeWidth = 2.4;
            fill = finding.status === 'missing' ? '#dddddd' : `${statusDef.color}33`;
        } else if (finding) {
            // Note-only finding: mark the box as touched without claiming a tilstand.
            stroke = '#888888';
            strokeWidth = 2.4;
        }

        const isSelected = selectedTeeth.includes(box.id);
        const cls = `dc-decid${readOnly ? ' dc-decid-readonly' : ''}${isSelected ? ' dc-decid-selected' : ''}`;
        const handleClick = readOnly ? undefined : e => this.handleToothClick(box.id, e);

        return (
            <g key={box.id}>
                <rect
                    id={`d-${box.id}`}
                    data-deciduous={box.id}
                    class={cls}
                    x={box.x}
                    y={box.y}
                    width={DECID_BOX_W}
                    height={DECID_BOX_H}
                    rx='3'
                    fill={fill}
                    stroke={stroke}
                    stroke-width={strokeWidth}
                    onClick={handleClick}
                />
                <text class='dc-decid-label' x={box.x + 4} y={box.y + 14}>{box.id}</text>
            </g>
        );
    };

    renderLegend(viewBoxOriginY, hasDeciduous) {
        const y = viewBoxOriginY;
        return (
            <g>
                <rect class='dc-tooth dc-tooth-readonly' x='505' y={y} width='44' height='20' fill={TOOTH_COLORS.incisor} stroke='currentColor' stroke-width='1.6' />
                <text class='dc-jaw-label' x='560' y={y + 16}><Text id='dental.legend.incisors'>INCISORS</Text></text>
                <rect class='dc-tooth dc-tooth-readonly' x='505' y={y + 42} width='44' height='20' fill={TOOTH_COLORS.canine} stroke='currentColor' stroke-width='1.6' />
                <text class='dc-jaw-label' x='560' y={y + 58}><Text id='dental.legend.canine'>CANINE</Text></text>
                <rect class='dc-tooth dc-tooth-readonly' x='505' y={y + 84} width='44' height='20' fill={TOOTH_COLORS.premolar} stroke='currentColor' stroke-width='1.6' />
                <text class='dc-jaw-label' x='560' y={y + 100}><Text id='dental.legend.premolars'>PREMOLARS</Text></text>
                <rect class='dc-tooth dc-tooth-readonly' x='505' y={y + 126} width='44' height='20' fill={TOOTH_COLORS.molar} stroke='currentColor' stroke-width='1.6' />
                <text class='dc-jaw-label' x='560' y={y + 142}><Text id='dental.legend.molars'>MOLARS</Text></text>
                {hasDeciduous && (
                    <g>
                        <rect class='dc-decid dc-decid-readonly' x='505' y={y + 168} width={DECID_BOX_W} height={DECID_BOX_H} rx='3' fill='none' stroke='currentColor' stroke-width='1.2' />
                        <text class='dc-decid-label' x='509' y={y + 182}>501</text>
                        <text class='dc-jaw-label' x='560' y={y + 184}><Text id='dental.legend.deciduous'>DECIDUOUS</Text></text>
                    </g>
                )}
            </g>
        );
    }

    renderChart(species, speciesId) {
        const cat = isCatSpecies(species, speciesId);
        const dog = isDogSpecies(species, speciesId);
        const horse = isHorseSpecies(species, speciesId);
        if (!cat && !dog && !horse) return null;

        let viewBox = DOG_VIEWBOX;
        let teeth = DOG_TEETH;
        let deciduous = DOG_DECIDUOUS;
        let Static = DogChartStatic;
        let Labels = DogChartLabels;
        let legendY = 600;
        if (cat) {
            viewBox = CAT_VIEWBOX;
            teeth = CAT_TEETH;
            deciduous = CAT_DECIDUOUS;
            Static = CatChartStatic;
            Labels = CatChartLabels;
            legendY = 355;
        } else if (horse) {
            viewBox = HORSE_VIEWBOX;
            teeth = HORSE_TEETH;
            deciduous = [];
            Static = HorseChartStatic;
            Labels = HorseChartLabels;
            legendY = 480;
        }

        return (
            <svg
                xmlns='http://www.w3.org/2000/svg'
                viewBox={viewBox}
                class='w-100 text-body'
                style='max-height: 800px;'
            >
                <Static />
                {teeth.map(this.renderTooth)}
                <Labels />
                {deciduous.map(this.renderDeciduous)}
                {this.renderLegend(legendY, deciduous.length > 0)}
            </svg>
        );
    }

    renderStatusPicker() {
        const { selectedTeeth } = this.state;
        const { readOnly, findings = [] } = this.props;
        if (!selectedTeeth.length || readOnly) return null;

        const selected = sortTeeth(selectedTeeth);
        const multiple = selected.length > 1;
        const allDeciduous = selected.every(isDeciduousId);
        const selectedFindings = selected.map(t => findings.find(f => f.tooth === t) || {});

        // A tilstand only reads as active when every selected tooth carries it -
        // otherwise the picker would claim a status the selection does not share.
        const statuses = selectedFindings.map(f => f.status || '');
        const currentStatus = statuses.every(s => s === statuses[0]) ? statuses[0] : '';

        // Same for the note: when the selected teeth disagree the field starts
        // blank, and typing then replaces the note on all of them.
        const notes = selectedFindings.map(f => f.note || '');
        const currentNote = notes.every(n => n === notes[0]) ? notes[0] : '';

        return (
            <div class='card mb-3'>
                <div class='card-body'>
                    <div class='d-flex justify-content-between align-items-center mb-2'>
                        <h5 class='card-title mb-0'>
                            {allDeciduous && multiple && <Text id='dental.deciduousTeethLabel'>Deciduous teeth</Text>}
                            {allDeciduous && !multiple && <Text id='dental.deciduousToothLabel'>Deciduous tooth</Text>}
                            {!allDeciduous && multiple && <Text id='dental.teethLabel'>Teeth</Text>}
                            {!allDeciduous && !multiple && <Text id='dental.toothLabel'>Tooth</Text>}
                            {' '}
                            {selected.join(', ')}
                        </h5>
                        <div class='d-flex align-items-center gap-2'>
                            {multiple && (
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary'
                                    onClick={this.closePicker}
                                >
                                    <Text id='dental.clearSelection'>Clear selection</Text>
                                </button>
                            )}
                            <button type='button' class='btn-close' aria-label='Close' onClick={this.closePicker} />
                        </div>
                    </div>
                    <div class='d-flex flex-wrap gap-2 mb-3'>
                        {STATUS_DEFS.map(s => {
                            const active = currentStatus === s.code;
                            const style = active
                                ? `background:${s.color}; border-color:${s.color}; color:#fff;`
                                : `border-color:${s.color}; color:${s.color};`;
                            return (
                                <button
                                    key={s.code}
                                    type='button'
                                    class='btn btn-sm'
                                    style={style}
                                    onClick={() => this.setStatus(s.code)}
                                >
                                    <Text id={`dental.status.${s.code}`}>{s.labelEn}</Text>
                                </button>
                            );
                        })}
                    </div>
                    <Localizer>
                        <input
                            type='text'
                            class='form-control'
                            value={currentNote}
                            placeholder={multiple
                                ? (
                                    <Text id='dental.notePlaceholderMulti' fields={{ count: selected.length }}>
                                        Note for the selected teeth...
                                    </Text>
                                )
                                : <Text id='dental.notePlaceholder'>Note for this tooth...</Text>}
                            onInput={this.setNote}
                        />
                    </Localizer>
                    <div class='small text-body-secondary mt-2'>
                        <Text id='dental.multiSelectHint'>
                            Hold Ctrl/Cmd or Shift and click to select several teeth at once.
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    renderSummary() {
        const { findings = [] } = this.props;
        if (!findings.length) return null;
        return (
            <div class='small text-body-secondary mb-2'>
                <Text id='dental.findingsCount' fields={{ count: findings.length }}>
                    {findings.length} dental findings recorded
                </Text>
            </div>
        );
    }

    // Clicking a chip in the findings list jumps to that one tooth, replacing
    // whatever multi-selection was active.
    selectTooth = (toothId) => {
        this.setState({ selectedTeeth: [toothId] });
    };

    removeFinding = (e, toothId) => {
        e.stopPropagation();
        const { findings = [], readOnly } = this.props;
        if (readOnly) return;
        const next = findings.filter(f => f.tooth !== toothId);
        this.setState(({ selectedTeeth }) => ({
            selectedTeeth: selectedTeeth.filter(t => t !== toothId),
        }));
        this.emitChange(next);
    };

    renderFindingsList() {
        const { findings = [], readOnly } = this.props;
        if (!findings.length) return null;

        const sorted = [...findings].sort((a, b) => String(a.tooth).localeCompare(String(b.tooth)));

        return (
            <div class='mb-3 d-flex flex-wrap gap-2'>
                {sorted.map(f => {
                    const statusDef = STATUS_LOOKUP[f.status];
                    const isSelected = this.state.selectedTeeth.includes(f.tooth);
                    // A finding with no tilstand picked (setNote creates one with
                    // status: '') must NOT be labelled "Normal" — the vet typed a note
                    // precisely because the tooth was not normal (TICKET-01400).
                    const statusLabel = statusDef
                        ? <Text id={`dental.status.${f.status}`}>{statusDef.labelEn}</Text>
                        : <Text id='dental.status.notSet'>Not set</Text>;
                    const borderColor = statusDef ? statusDef.color : '#888888';
                    const bg = isSelected ? '#e7f1ff' : 'transparent';
                    const style = `border: 2px solid ${borderColor}; background:${bg}; cursor:pointer;`;
                    return (
                        <div
                            key={f.tooth}
                            class='d-inline-flex align-items-center px-2 py-1 rounded'
                            style={style}
                            onClick={() => this.selectTooth(f.tooth)}
                            title={f.note || ''}
                        >
                            <strong class='me-2'>{f.tooth}</strong>
                            <span class='small me-2' style={`color:${borderColor};`}>{statusLabel}</span>
                            {f.note && (
                                <span class='small text-body-secondary me-2 text-truncate' style='max-width: 180px;'>
                                    {f.note}
                                </span>
                            )}
                            {!readOnly && (
                                <button
                                    type='button'
                                    class='btn-close btn-close-sm ms-1'
                                    style='font-size: 0.6rem;'
                                    aria-label='Remove'
                                    onClick={e => this.removeFinding(e, f.tooth)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderUnsupported() {
        return (
            <div class='alert alert-info small mb-0'>
                <Text id='dental.unsupportedSpecies'>Dental chart is only available for dogs and cats.</Text>
            </div>
        );
    }

    render() {
        const { species, speciesId } = this.props;
        const cat = isCatSpecies(species, speciesId);
        const dog = isDogSpecies(species, speciesId);
        const horse = isHorseSpecies(species, speciesId);
        if (!cat && !dog && !horse) {
            return this.renderUnsupported();
        }

        return (
            <div class='dental-chart'>
                <style>{STYLES}</style>
                {this.renderSummary()}
                {this.renderFindingsList()}
                {this.renderStatusPicker()}
                {this.renderChart(species, speciesId)}
            </div>
        );
    }
}

export default DentalChart;
export {
    STATUS_DEFS,
    TOOTH_COLORS,
    isCatSpecies,
    isDogSpecies,
    isHorseSpecies,
    nextSelection,
    applyStatusToTeeth,
    applyNoteToTeeth,
    deciduousIdFor,
    permanentIdFor,
    isDeciduousId,
};
